import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { RestLink } from 'apollo-link-rest';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { customFetch } from '../utils/customFetch';
import { splitLink } from '../utils/splitLink';
import { createAuthHeaders, useAuth } from './AuthorizationProvider';
import { useConfig } from './ConfigurationProvider';
import { CustomWebSocketLink } from './websockets/CustomWSLink';

export const DataProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { restPath, tenant, wsUrl } = useConfig();
  const auth = useAuth();
  const { t } = useTranslation();

  const restLink = new RestLink({
    uri: restPath,
    customFetch: customFetch(t, auth.forceUpdateToken),
  });

  // prevent creating multiple clients on rerender!
  const wsLink = useMemo(
    () => new CustomWebSocketLink(wsUrl, tenant, auth),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // destroy old WS client
  useEffect(() => {
    return () => {
      wsLink.destroy();
    };
  }, [wsLink]);

  const authLink = setContext(async (_, { headers }) => {
    if (auth.isTokenInvalid()) {
      // token needs to be revoked and stored in Apollo's context before execution of request will be handled to customFetch
      await auth.forceUpdateToken();

      console.debug('Token was evaluated as invalid and needs to be re-fetched before request will be executed.');
    }

    return {
      headers: {
        ...headers,
        ...createAuthHeaders(auth.keycloak.token!, tenant),
      },
    };
  });

  const client = new ApolloClient({
    link: authLink.concat(splitLink(wsLink, restLink)),
    cache: new InMemoryCache({
      typePolicies: {
        RequestPreview: {
          keyFields: ['type', 'id'],
        },
        SupervisorRequestPreview: {
          keyFields: ['type', 'id'],
        },
      },
    }),
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
